import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { Alert } from '@central/types';
import { api } from '@/lib/api';

interface AlertsResponse {
  alerts: Alert[];
  unreadCount: number;
}

export function useAlerts(options: { unreadOnly?: boolean } = {}): UseQueryResult<AlertsResponse> {
  const qs = options.unreadOnly ? '?unreadOnly=true' : '';
  return useQuery({
    queryKey: ['alerts', options],
    queryFn: () => api<AlertsResponse>(`/api/alerts${qs}`),
    refetchInterval: 60_000,
  });
}

export function useUnreadCount(): UseQueryResult<{ count: number }> {
  return useQuery({
    queryKey: ['alerts', 'unread-count'],
    queryFn: () => api<{ count: number }>('/api/alerts/unread-count'),
    refetchInterval: 60_000,
  });
}

export function useMarkAlertRead(): ReturnType<typeof useMutation<Alert, Error, number>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api<Alert>(`/api/alerts/${id}/read`, { method: 'PATCH' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
}

export function useMarkAllAlertsRead(): ReturnType<typeof useMutation<{ updated: number }>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ updated: number }>('/api/alerts/mark-all-read', { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
}
